import {
  mergeVariantAvailabilityData,
  variantIdToGid,
} from './availability';
import {
  fetchOnlineFulfillmentStock,
  getOnlineQuantityForVariant,
} from './online-fulfillment-stock';
import { fetchVariantAvailability } from './storefront-api';
import { updateClickCollectOnlyBadge } from './click-collect-only-badge';

const availabilityCache = new Map();

export function clearVariantAvailabilityCache(variantId = null) {
  if (variantId == null) {
    availabilityCache.clear();
    return;
  }

  availabilityCache.delete(variantId);
}

function syncClickCollectOnlyBadge(variantData) {
  if (variantData?.onlineStockStatus !== 'ready') {
    updateClickCollectOnlyBadge({ onlineQuantity: null, tags: variantData?.tags ?? [] });
    return;
  }

  updateClickCollectOnlyBadge({
    onlineQuantity: variantData?.onlineQuantity,
    tags: variantData?.tags ?? [],
  });
}

function withPendingOnlineStock(variantData) {
  return {
    ...variantData,
    onlineQuantity: null,
    onlineStockStatus: 'pending',
  };
}

function withReadyOnlineStock(variantData, onlineQuantity) {
  return {
    ...variantData,
    onlineQuantity: Number(onlineQuantity) || 0,
    onlineStockStatus: 'ready',
  };
}

export async function enrichWithStorefrontAvailability(baseVariantData) {
  if (!baseVariantData?.variantId) {
    return withPendingOnlineStock(baseVariantData);
  }

  const { variantId } = baseVariantData;
  const cachedAvailability = availabilityCache.get(variantId);

  if (cachedAvailability?.onlineStockStatus === 'ready') {
    const enrichedFromCache = mergeVariantAvailabilityData(baseVariantData, cachedAvailability);
    const ready = withReadyOnlineStock(
      enrichedFromCache,
      cachedAvailability.onlineQuantity
    );
    syncClickCollectOnlyBadge(ready);
    return ready;
  }

  try {
    const apiAvailability = await fetchVariantAvailability(variantIdToGid(variantId));
    const merged = mergeVariantAvailabilityData(baseVariantData, {
      ...apiAvailability,
      onlineQuantity: null,
    });
    const pending = withPendingOnlineStock({
      ...merged,
      storefrontOnlineQuantity: Number(apiAvailability.onlineQuantity) || 0,
    });

    availabilityCache.set(variantId, {
      ...apiAvailability,
      onlineQuantity: null,
      onlineStockStatus: 'pending',
      storefrontOnlineQuantity: pending.storefrontOnlineQuantity,
    });

    syncClickCollectOnlyBadge(pending);
    return pending;
  } catch (fetchError) {
    console.error('Store locator variant availability fetch failed:', fetchError);

    const pending = withPendingOnlineStock({
      ...baseVariantData,
      storeAvailabilities: baseVariantData.storeAvailabilities ?? [],
      storefrontOnlineQuantity: 0,
    });

    availabilityCache.set(variantId, {
      onlineQuantity: null,
      onlineStockStatus: 'pending',
      storeAvailabilities: [],
      storefrontOnlineQuantity: 0,
    });

    syncClickCollectOnlyBadge(pending);
    return pending;
  }
}

export async function enrichWithOnlineFulfillmentStock(variantData) {
  if (!variantData?.variantId) {
    return withReadyOnlineStock(variantData ?? {}, 0);
  }

  const { variantId } = variantData;
  const cachedAvailability = availabilityCache.get(variantId);

  if (cachedAvailability?.onlineStockStatus === 'ready') {
    const ready = withReadyOnlineStock(
      mergeVariantAvailabilityData(variantData, cachedAvailability),
      cachedAvailability.onlineQuantity
    );
    syncClickCollectOnlyBadge(ready);
    return ready;
  }

  const storefrontFallback = Number(variantData.storefrontOnlineQuantity) || 0;
  let onlineQuantity = storefrontFallback;

  if (variantData.productId) {
    try {
      const totalsByVariantId = await fetchOnlineFulfillmentStock(variantData.productId);
      const resolvedQuantity = getOnlineQuantityForVariant(
        totalsByVariantId,
        variantData.variantId
      );

      if (resolvedQuantity != null) {
        onlineQuantity = resolvedQuantity;
      }
    } catch (onlineStockError) {
      console.error('Online fulfillment stock fetch failed:', onlineStockError);
      onlineQuantity = storefrontFallback;
    }
  }

  const ready = withReadyOnlineStock(variantData, onlineQuantity);

  availabilityCache.set(variantId, {
    ...(cachedAvailability ?? {}),
    storeAvailabilities: ready.storeAvailabilities ?? cachedAvailability?.storeAvailabilities ?? [],
    onlineQuantity: ready.onlineQuantity,
    onlineStockStatus: 'ready',
    storefrontOnlineQuantity: storefrontFallback,
  });

  syncClickCollectOnlyBadge(ready);
  return ready;
}

/** @deprecated Prefer enrichWithStorefrontAvailability + enrichWithOnlineFulfillmentStock */
export async function getEnrichedVariantData(baseVariantData) {
  const withStores = await enrichWithStorefrontAvailability(baseVariantData);
  return enrichWithOnlineFulfillmentStock(withStores);
}
